import { UnauthorizedError } from '@blocksuite/blocks';
import { type AuthAtom, authAtom } from '@pique/core/atoms';
import { getCurrentStore } from '@toeverything/infra';

import { BackendError } from '../modules/cloud/error';
import type { FetchService } from '../modules/cloud/services/fetch';
import { commands as authCommands } from '../tauri/plugins/auth';

export interface AwsCredentials {
  accessKeyId: string;
  secretAccessKey: string;
  region: string;
  sessionToken?: string;
}

export async function getAwsCredentials(): Promise<AwsCredentials | null> {
  if (!environment.isTauri) {
    return null;
  }

  try {
    const userId = await authCommands.getFromStore('auth-user-id');
    if (!userId) {
      return null;
    }

    const accessKeyId = await authCommands.getFromVault('aws-access-key-id');
    const secretAccessKeyRaw = await authCommands.getFromVault(
      'aws-secret-access-key'
    );
    const region = await authCommands.getFromVault('aws-region');

    if (accessKeyId && secretAccessKeyRaw && region) {
      const [secretAccessKey, sessionToken] = secretAccessKeyRaw.includes('|')
        ? secretAccessKeyRaw.split('|', 2)
        : [secretAccessKeyRaw, undefined];

      return {
        accessKeyId,
        secretAccessKey,
        region,
        sessionToken,
      };
    }

    return null;
  } catch (error) {
    console.warn('[getAwsCredentials] Error accessing AWS credentials:', error);
    return null;
  }
}

export async function setAwsCredentials(
  credentials: AwsCredentials
): Promise<void> {
  if (!environment.isTauri) {
    return;
  }

  try {
    const userId = await authCommands.getFromStore('auth-user-id');
    if (!userId) {
      return;
    }

    await authCommands.setInVault('aws-access-key-id', credentials.accessKeyId);
    await authCommands.setInVault(
      'aws-secret-access-key',
      credentials.secretAccessKey
    );
    await authCommands.setInVault('aws-region', credentials.region);
  } catch (error) {
    console.error('[setAwsCredentials] Error saving AWS credentials:', error);
    throw error;
  }
}

export async function hasAwsCredentials(): Promise<boolean> {
  const credentials = await getAwsCredentials();
  return credentials !== null;
}

export async function configureAwsAsPreferred(
  credentials: AwsCredentials
): Promise<void> {
  if (!environment.isTauri) {
    throw new Error('AWS transcription is only available in desktop mode');
  }

  try {
    await setAwsCredentials(credentials);

    const flagsModule = await import('../tauri/plugins/flags');
    const flagsCommands = flagsModule.commands;

    const currentConfig = await flagsCommands.getCloudPreviewConfig();
    const updatedConfig = {
      ...currentConfig,
      preferred_provider: 'AwsTranscribe' as const,
    };

    await flagsCommands.setCloudPreviewConfig(updatedConfig);
  } catch (error) {
    console.error('[configureAwsAsPreferred] Error configuring AWS:', error);
    throw error;
  }
}

export interface AwsTranscribeCredentials {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken: string;
  region: string;
  expiration: Date;
}

export async function getAwsTranscribeCredentials(
  fetchService: FetchService,
  attempt = 1,
  maxRetries = 3,
  cachedCredentials?: AwsTranscribeCredentials
): Promise<AwsTranscribeCredentials> {
  if (cachedCredentials && cachedCredentials.expiration > new Date()) {
    return cachedCredentials;
  }

  try {
    const response = await fetchService.fetch(
      '/api/copilot/aws-transcribe-credentials'
    );
    const data = await response.json();

    if (
      environment.isTauri &&
      data.accessKeyId &&
      data.secretAccessKey &&
      data.region
    ) {
      try {
        const userId = await authCommands.getFromStore('auth-user-id');
        if (userId) {
          try {
            await authCommands.setInVault(
              'aws-access-key-id',
              data.accessKeyId
            );
            await authCommands.setInVault(
              'aws-secret-access-key',
              data.secretAccessKey
            );
            await authCommands.setInVault('aws-region', data.region);
            if (data.sessionToken) {
              await authCommands.setInVault(
                'aws-secret-access-key',
                `${data.secretAccessKey}|${data.sessionToken}`
              );
            }
          } catch (e) {
            console.warn(
              '[getAwsTranscribeCredentials] Failed to save to vault:',
              e
            );
          }
        }
      } catch (error) {
        console.warn(
          '[getAwsTranscribeCredentials] Error saving to vault:',
          error
        );
      }
    }

    return {
      accessKeyId: data.accessKeyId,
      secretAccessKey: data.secretAccessKey,
      sessionToken: data.sessionToken || '',
      region: data.region,
      expiration: data.expiration
        ? new Date(data.expiration)
        : new Date(Date.now() + 60 * 60 * 1000),
    };
  } catch (error) {
    if (error instanceof BackendError && error.status === 401) {
      try {
        const currentAuth = getCurrentStore().get(authAtom);
        (
          getCurrentStore().set as (
            atom: typeof authAtom,
            value: AuthAtom
          ) => void
        )(authAtom, { ...currentAuth, openModal: true });
      } catch {}
      throw new UnauthorizedError();
    }

    if (error instanceof UnauthorizedError) {
      throw error;
    }

    if (attempt < maxRetries) {
      const delay = Math.min(1000 * Math.pow(2, attempt - 1), 10000);
      await new Promise(resolve => setTimeout(resolve, delay));
      return getAwsTranscribeCredentials(fetchService, attempt + 1, maxRetries);
    }

    throw error;
  }
}
